import { useRef } from 'react';

import { Button } from '@bambi/component-library/src/components/Button';
import { ClipboardIcon } from '@heroicons/react/24/outline';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { Modal } from 'src/common/primitives/Modal';
import { useCopyText } from 'src/features/billing/invoices/modals/hooks/useCopyText';

import { settingSlice } from '../setting.slice';
import {
  EMBED_COPY_WARNING,
  generateTripRequestEmbedHTML,
} from './generateTripRequestEmbedHTML';

export function TripRequestEmbedCopyModal() {
  const preRef = useRef<HTMLPreElement>(null);

  const handlePreClick = () => {
    if (preRef.current) {
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(preRef.current);
      selection?.removeAllRanges();
      selection?.addRange(range);
      handleCopyClick();
    }
  };

  const dispatch = useAppDispatch();
  const { isOpen, tripRequestEmbed } = useAppSelector(
    (state) => state.setting.tripRequestEmbedCopyModal
  );
  const handleCopyText = useCopyText();
  const handleCopyClick = () => {
    if (tripRequestEmbed) {
      handleCopyText(generateTripRequestEmbedHTML(tripRequestEmbed));
    }
  };

  if (!tripRequestEmbed) {
    return null;
  }

  return (
    <Modal
      open={isOpen}
      setOpen={() =>
        dispatch(settingSlice.actions.closeTripRequestEmbedCopyModal())
      }
      onClose={() =>
        dispatch(settingSlice.actions.closeTripRequestEmbedCopyModal())
      }
      title={`Copy Trip Request Embed: ${tripRequestEmbed.label}`}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-row items-center gap-1">
          <h2 className="text-lg font-medium">
            How to use the Trip Request Embed
          </h2>
        </div>
        <ol className="list-inside list-decimal">
          <li>Copy the code below</li>
          <li>
            Have your web developer paste the <strong>exact</strong> code
            snippet into your website
          </li>
          <li>Test the form by submitting a trip request from your website</li>
          <li>
            Verify the trip request was received under Trips &gt; All Requests
          </li>
        </ol>
        <div
          className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800"
          role="status"
          aria-live="polite"
        >
          <strong>Important:</strong> {EMBED_COPY_WARNING}
        </div>
        <code className="relative block max-w-full cursor-pointer overflow-auto break-all rounded bg-gray-100 p-3 font-mono text-xs text-gray-900">
          <div className="absolute top-0 right-0">
            <Button
              variant="icon"
              onClick={handleCopyClick}
              dataTestId="copy-button"
            >
              <ClipboardIcon className="w-6" />
            </Button>
          </div>
          <pre
            ref={preRef}
            onClick={handlePreClick}
            title="Click to select and copy the code"
            data-testid="trip-request-embed-code"
          >
            {generateTripRequestEmbedHTML(tripRequestEmbed)}
          </pre>
        </code>
        <div className="flex flex-row items-center justify-end gap-4">
          <Button
            variant="primary"
            onClick={() => {
              dispatch(settingSlice.actions.closeTripRequestEmbedCopyModal());
            }}
          >
            Done
          </Button>
        </div>
      </div>
    </Modal>
  );
}
